import { Controller } from "@hotwired/stimulus"

// Controlador Stimulus para seções expansíveis em páginas de detalhes (show)
// 
// Uso básico:
// 1. Adicione o atributo data-controller="expandable-sections" no elemento container
// 2. Use os seguintes data attributes:
//    - data-expandable-sections-target="section" em cada seção
//    - data-section-id="unique-id" em cada seção (obrigatório)
//    - data-action="click->expandable-sections#toggle" no cabeçalho de cada seção
//    - data-expandable-sections-target="toggleIcon" no botão de expandir/recolher todas as seções (opcional)
//    - data-default-open="true" em seções que devem começar expandidas (opcional)
//
// Exemplo de estrutura HTML:
/*
<div data-controller="expandable-sections">
  <div class="actions">
    <div data-action="click->expandable-sections#toggleAll" 
         data-expandable-sections-target="toggleIcon" 
         title="Expandir/Colapsar Todas as Seções">
      <i class="zmdi zmdi-unfold-less"></i>
    </div>
  </div>

  <div data-expandable-sections-target="section" data-section-id="basic-info">
    <div class="card-header" data-action="click->expandable-sections#toggle">
      <span class="show-section-arrow">
        <i class="zmdi zmdi-chevron-down nav-arrow"></i>
      </span>
      <h4>Seção Básica</h4>
    </div>
    <div class="card-body show-section-body">
      <!-- Conteúdo da seção -->
    </div>
  </div>

  <div data-expandable-sections-target="section" 
       data-section-id="details-info" 
       data-default-open="true">
    <div class="card-header" data-action="click->expandable-sections#toggle">
      <span class="show-section-arrow">
        <i class="zmdi zmdi-chevron-down nav-arrow"></i>
      </span>
      <h4>Seção de Detalhes (aberta por padrão)</h4>
    </div>
    <div class="card-body show-section-body">
      <!-- Conteúdo da seção -->
    </div>
  </div>
</div>
*/
export default class extends Controller {
  static targets = ["section", "arrow", "toggleIcon"]
  
  connect() {
    // Percorre todas as seções
    this.sectionTargets.forEach((section, index) => {
      const body = section.querySelector('.show-section-body')
      const arrow = section.querySelector('.nav-arrow')
      
      if (!body || !arrow) return

      if (section.closest('form')) {
        // No formulário, todos os cards começam maximizados
        body.classList.remove('show-section-collapsed')
        arrow.style.transform = 'rotate(0deg)'
        arrow.classList.add('section-open')
      } else {
        // Verifica se a seção deve estar aberta por padrão
        const defaultOpen = section.dataset.defaultOpen === 'true'
        
        // Na página de show, primeira seção maximizada, outras minimizadas
        // Exceção: se tiver data-default-open="true", também fica maximizada
        if (index === 0 || defaultOpen) {
          body.classList.remove('show-section-collapsed')
          arrow.style.transform = 'rotate(0deg)'
          arrow.classList.add('section-open')
        } else {
          body.classList.add('show-section-collapsed')
          arrow.style.transform = 'rotate(180deg)'
          arrow.classList.remove('section-open')
        }
      }
    })
  }

  toggle(event) {
    const sectionElement = event.currentTarget.closest('[data-section-id]')
    if (!sectionElement) return

    const sectionId = sectionElement.dataset.sectionId
    
    const body = sectionElement.querySelector('.show-section-body')
    const arrow = sectionElement.querySelector('.nav-arrow')
    
    if (!body || !arrow) return
    
    // Permite minimizar/maximizar a primeira seção
    body.classList.toggle('show-section-collapsed')
    
    if (body.classList.contains('show-section-collapsed')) {
      arrow.style.transform = 'rotate(180deg)' // Minimizada, seta para baixo
      arrow.classList.remove('section-open')
    } else {
      arrow.style.transform = 'rotate(0deg)' // Expandida, seta para cima
      arrow.classList.add('section-open')
    }
  }

  toggleAll() {
    const sectionsToToggle = this.sectionTargets.slice(1)
    const allExpanded = sectionsToToggle.every(section => 
      section.querySelector('.show-section-body').classList.contains('show-section-collapsed')
    )
    
    sectionsToToggle.forEach(section => {
      const body = section.querySelector('.show-section-body')
      const arrow = section.querySelector('.nav-arrow')
      
      if (body && arrow) {
        if (allExpanded) {
          body.classList.remove('show-section-collapsed')
          arrow.style.transform = 'rotate(0deg)' // Expandida, seta para cima
          arrow.classList.add('section-open')
        } else {
          body.classList.add('show-section-collapsed')
          arrow.style.transform = 'rotate(180deg)' // Minimizada, seta para baixo
          arrow.classList.remove('section-open')
        }
      }
    })
    
    // Atualiza o ícone global
    if (this.hasToggleIconTarget) {
      const iconElement = this.toggleIconTarget.querySelector('i')
      iconElement.className = allExpanded ? 'zmdi zmdi-unfold-more' : 'zmdi zmdi-unfold-less'
    }
  }
} 